import {
  ContentLayout,
  ContentMain,
  ContentTip,
} from 'components/ContentLayout'
import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './Billing.sass'
import ProfileViewModel from '../ProfileViewModel'
import ProfileHeader from '../ProfileHeader'
import { ScrollOffsetInterpolation } from 'hooks/spring-hooks'
import { useActivation } from 'hooks/mobx-hooks'
import { TabBar, TabItem } from 'components/TabBar'
import { useDocumentTitle } from 'hooks/document-title'
import StripeConnectPayoutModal from 'billing/components/StripeConnectPayoutModal'
import PayoutMethod from './PayoutMethod'
import { Route, RouteChildrenProps, useRouteMatch } from 'react-router'
import PaymentMethods from './PaymentMethods'
import link from 'infra/link'

interface BillingProps extends RouteChildrenProps {
  vm: ProfileViewModel
  scrollOffset: ScrollOffsetInterpolation
}

export default observer(function Billing({ vm, scrollOffset }: BillingProps) {
  useActivation(vm.billing)
  useDocumentTitle('Billing')

  return (
    <div className={cx(styles.root)}>
      <ProfileHeader
        viewTitle="Billing"
        vm={vm}
        scrollOffset={scrollOffset}
        tabBar={
          <TabBar align="left">
            <BillingTabItem to={link.profileBillingPaymentMethods()}>
              Payment Methods
            </BillingTabItem>

            {vm.billing.isPayoutsEnabled && (
              <BillingTabItem to={link.profileBillingPayoutMethod()}>
                Payout Method
              </BillingTabItem>
            )}
          </TabBar>
        }
      />
      <ContentLayout accountForTabBar accountForLabel>
        <Route path={link.profileBillingPaymentMethods()}>
          <ContentMain>
            <PaymentMethods vm={vm.billing.paymentMethodsVM} />
          </ContentMain>
          <ContentTip
            illustrationUrl={require('assets/images/img_workspace-billing.svg')}
            title="Transactions don't have to be complicated"
          >
            Are you looking to get more out of your Workspace? Our in-depth Help
            Center can help!
          </ContentTip>
        </Route>
        {vm.billing.isPayoutsEnabled && (
          <Route path={link.profileBillingPayoutMethod()}>
            <ContentMain>
              <PayoutMethod vm={vm.billing.payoutMethodVM} />
            </ContentMain>

            <ContentTip
              illustrationUrl={require('assets/images/img_payment-methods.svg')}
              title="Review your payout method"
            >
              We’ve made some changes to our payout system and only allow one
              payout method per user.
            </ContentTip>
          </Route>
        )}
      </ContentLayout>

      <StripeConnectPayoutModal
        showing={vm.billing.stripeConnectModalVM.showing}
        state={vm.billing.completeStripeConnectFlow.match({
          pending: () => 'pending',
          rejected: () => 'error',
          resolved: () => 'done',
        })}
        onCloseRequest={vm.billing.stripeConnectModalVM.close}
        error={
          vm.billing.completeStripeConnectFlow.rejected
            ? vm.billing.completeStripeConnectFlow.error
            : null
        }
      />
    </div>
  )
})

interface BillingTabItemProps {
  to: string
  children?: React.ReactNode
}

const BillingTabItem = observer(function BillingTabItem({
  to,
  children,
}: BillingTabItemProps) {
  return (
    <TabItem active={useRouteMatch(to)?.isExact} to={to}>
      {children}
    </TabItem>
  )
})
